import { ChoiceList } from "@cmsgov/ds-medicare-gov";
import React, { useContext, ChangeEvent } from "react";
import { FormattedHTMLMessage } from "react-intl-phraseapp";
import { ActionType, PlanCategoryFilters } from "../../@types";
import { CONSTANTS as C } from "../../helpers/CONSTANTS";
import { useTranslate, sendTealiumEvent } from "../../helpers";
import { AppStore } from "../../app/store";
import { defaultState } from "../../app/store/AppStore";
import { HelpDrawerWrapper } from "../../components";

export interface CategoryFilterGroupProps {
  availableCategories?: PlanCategoryFilters;
  onFilterUpdate: (filters: string[]) => void;
}

const CategoryFilterGroup: React.FunctionComponent<CategoryFilterGroupProps> = ({
  availableCategories,
  onFilterUpdate,
}) => {
  const {
    state: {
      planType,
      searchResultsFilters = defaultState.searchResultsFilters,
      language,
    },
    dispatch,
  } = useContext(AppStore.AppContext);
  const { planCategoryFilters } = searchResultsFilters;
  const t = useTranslate();

  const categoryFilterMap = {
    hmo: "PLAN_FILTER_HMO",
    ppo: "PLAN_FILTER_PPO",
    pffs: "PLAN_FILTER_PFFS",
    msa: "PLAN_FILTER_MSA",
    cost: "PLAN_FILTER_COST",
  };

  const handleChecking = (event: ChangeEvent<HTMLInputElement>): void => {
    const { checked, value } = event.currentTarget;
    let newFilters;

    // Get and send Tealium values
    if (checked) {
      sendTealiumEvent("Find Plans - Available Plans", value);
      newFilters = [...planCategoryFilters, value];
    } else {
      newFilters = planCategoryFilters.filter(f => f !== value);
    }

    dispatch({
      type: ActionType.UPDATE_SEARCH_RESULTS_FILTERS,
      payload: {
        ...searchResultsFilters,
        planCategoryFilters: newFilters,
      },
    });

    onFilterUpdate(newFilters);
  };

  return (
    <div>
      {planType && availableCategories && (
        <ChoiceList
          name="PlanCategoryFilter"
          type="checkbox"
          label={
            <span className="PlanFilters__filterGroupHeadingName">
              {t("search_results_page.filters.types_of_plans")}
            </span>
          }
          className="PlanFilters__filterGroupHeading ds-u-margin--0"
          hint={
            <div className="PlanFilters__filterGroupHeadingHint">
              <HelpDrawerWrapper
                toggleText={t(
                  "search_results_page.filters.types_of_plans.help.toggle"
                )}
                drawerTitle={t(
                  "search_results_page.filters.types_of_plans.help.title"
                )}
                tealiumProps={{
                  callingLabel: "Find Plans - Available Plans",
                  label: "Learn about plan types",
                }}
              >
                <p>
                  {t("search_results_page.filters.types_of_plans.help.intro")}
                </p>
                <p>
                  <b>{t("search_results_page.filter_labels.hmo")}:</b>{" "}
                  {t("search_results_page.filters.types_of_plans.help.hmo")}
                </p>
                <p>
                  <b>{t("search_results_page.filter_labels.ppo")}:</b>{" "}
                  {t("search_results_page.filters.types_of_plans.help.ppo")}
                </p>
                <p>
                  <b>{t("search_results_page.filter_labels.pffs")}:</b>{" "}
                  {t("search_results_page.filters.types_of_plans.help.pffs")}
                </p>
                <p>
                  <b>{t("search_results_page.filter_labels.msa")}:</b>{" "}
                  {t("search_results_page.filters.types_of_plans.help.msa")}
                </p>
                <p>
                  <b>{t("search_results_page.filter_labels.cost")}:</b>{" "}
                  {t("search_results_page.filters.types_of_plans.help.cost")}
                </p>
                <ul>
                  <li>
                    <FormattedHTMLMessage id="search_results_page.filters.types_of_plans.help.cost_2" />
                  </li>
                  <li>
                    <FormattedHTMLMessage id="search_results_page.filters.types_of_plans.help.cost_3" />
                  </li>
                </ul>
                <p>
                  <b>{t("search_results_page.filter_labels.mmp")}:</b>{" "}
                  {t("search_results_page.filters.types_of_plans.help.mmp")}
                </p>
                <p>
                  <b>{t("search_results_page.filter_labels.special_needs")}:</b>{" "}
                  {t(
                    "search_results_page.filters.types_of_plans.help.special_needs"
                  )}
                </p>
                <p>
                  <a
                    href={C.linkToTypesOfHealthPlans(language)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(): void => {
                      sendTealiumEvent(
                        "Find Plans - Available Plans - Learn about plan types",
                        "learn more about Medicare health plans"
                      );
                    }}
                  >
                    {t(
                      "search_results_page.filters.types_of_plans.help.learn_more_link"
                    )}
                  </a>
                </p>
              </HelpDrawerWrapper>
            </div>
          }
          onChange={handleChecking}
          choices={Object.entries(availableCategories)
            .filter(([_cat, numPlans]) => numPlans)
            .map(([cat, _numPlans]) => {
              const filter = categoryFilterMap[cat];
              const filterNameKey = C.categoryFilters[filter];
              return {
                checked: !!planCategoryFilters.find(f => f === filter),
                label: t(filterNameKey),
                value: filter,
              };
            })}
        />
      )}
    </div>
  );
};

export default CategoryFilterGroup;
